import {
	faCartShopping,
	faCircleX,
	faLightEmergency,
	faPuzzle,
	faPuzzlePiece as faPuzzlePieceOutline,
	faSackDollar,
} from '@fortawesome/pro-light-svg-icons'
import {
	faPuzzlePiece as faPuzzlePieceSolid,
	faPuzzle as faPuzzleSolid,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tooltip } from '@mui/material'
import {
	GridCellParams,
	GridColDef,
	GridColumnGroupingModel,
	GridColumnVisibilityModel,
	GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { numToCurrency, numToPercentage } from '@utils/stringHelpers'
import { PurchaseGroupMatchType } from '@views/MarketBaskets/MarketBasketProvider'

export enum Statuses {
	Review = 'REVIEW',
	Basket = 'BASKET',
	All = 'ALL',
}

const iconMap = {
	[PurchaseGroupMatchType.SIMILAR_MATCH]: faSackDollar,
	[PurchaseGroupMatchType.EXACT]: faPuzzle,
	[PurchaseGroupMatchType.NO_MATCH]: faCircleX,
}

const cellFormatterPct = (field: string, minWidth: number) => ({
	field,
	minWidth,
	valueFormatter: (value) => `${value ? value.toFixed(2) : '--'}%`,
	cellClassName: (params: GridCellParams) => {
		const cellValue = params.value

		if (!Number.isNaN(cellValue) && Number(cellValue) < 0) {
			return 'negative-number'
		}
	},
})

const createCellFormatter = (
	productType: 'P' | 'M', // purchased or matched
	valueType: 'string' | 'number' | 'currency' | 'percentage', // type of value in the field
	field: string,
	minWidth = 50,
	currency?: string
) => ({
	field,
	minWidth,
	valueFormatter: (value) => {
		if (value === undefined || value === null || value === '') {
			return '--'
		}
		if (valueType === 'currency' && currency) {
			return numToCurrency(value, currency)
		}
		if (valueType === 'percentage') {
			return numToPercentage(value)
		}
		return value
	},
	cellClassName: (params: GridCellParams) => {
		const cellValue = params.value

		if (!Number.isNaN(cellValue) && Number(cellValue) < 0) {
			return productType === 'P' ? 'purchased-cell-negative-number' : 'negative-number'
		}

		if (productType === 'P') {
			return 'purchased-cell'
		}
	},
	renderCell: (params) => (
		<Tooltip
			title={valueType === 'currency' ? numToCurrency(params.value, currency, 5) : null}
			placement="top"
		>
			<span className="MuiDataGrid-cellContent">{params.formattedValue}</span>
		</Tooltip>
	),
})

// remove with feature-art-1621-pal-score-migration ff
export const scoreColumns = new Set(['switchLikelihood', 'matchedRecommendationScore'])

// Column definitions as map to handle ordering from user preferences
export function getColumnMap(currency: string): Map<string, GridColDef> {
	return new Map<string, GridColDef>([
		//
		// Purchased Item Columns
		//
		[
			'groupKeyDisplayName',
			{
				headerName: 'Owner',
				disableReorder: true,
				hideable: false,
				...createCellFormatter('P', 'string', 'groupKeyDisplayName', 250),
			},
		],
		[
			'manufacturerName',
			{
				headerName: 'Mfg',
				width: 200,
				...createCellFormatter('P', 'string', 'manufacturerName'),
			},
		],
		[
			'description',
			{
				headerName: 'Description',
				width: 250,
				...createCellFormatter('P', 'string', 'description'),
			},
		],
		[
			'qtyPurchased',
			{
				headerName: 'QTY',
				type: 'number',
				align: 'right',
				...createCellFormatter('P', 'number', 'qtyPurchased', 100),
			},
		],
		[
			'packSize',
			{
				headerName: 'Pack',
				...createCellFormatter('P', 'string', 'packSize', 100),
			},
		],
		[
			'pricePerCase',
			{
				headerName: 'Unit Price',
				type: 'number',
				align: 'right',
				...createCellFormatter('P', 'currency', 'pricePerCase', 50, currency),
			},
		],
		[
			'caseVolume',
			{
				headerName: 'Total Volume',
				type: 'number',
				align: 'right',
				...createCellFormatter('P', 'currency', 'caseVolume', 120, currency),
			},
		],
		[
			'brandName',
			{
				headerName: 'Brand',
				...createCellFormatter('P', 'string', 'brandName', 50),
			},
		],
		[
			'brandType',
			{
				headerName: 'Brd Type',
				...createCellFormatter('P', 'string', 'brandType', 50),
			},
		],
		[
			'mpc',
			{
				headerName: 'MPC',
				...createCellFormatter('P', 'string', 'mpc', 50),
			},
		],
		[
			'internalCategoryName',
			{
				headerName: 'Category',
				width: 150,
				...createCellFormatter('P', 'string', 'internalCategoryName'),
			},
		],
		[
			'dpc',
			{
				headerName: 'DPC',
				...createCellFormatter('P', 'string', 'dpc', 50),
			},
		],
		[
			'unit',
			{
				headerName: 'Unit',
				...createCellFormatter('P', 'string', 'unit', 50),
			},
		],
		[
			'distributorParentName',
			{
				headerName: 'Dist',
				width: 150,
				...createCellFormatter('P', 'string', 'distributorParentName'),
			},
		],
		[
			'distributorLocationName',
			{
				headerName: 'Dist Loc',
				width: 200,
				...createCellFormatter('P', 'string', 'distributorLocationName'),
			},
		],
		[
			'currentProgramType',
			{
				headerName: 'Cur Program',
				...createCellFormatter('P', 'string', 'currentProgramType', 200),
			},
		],
		[
			'exclusionSource',
			{
				headerName: 'Exclusion Source',
				...createCellFormatter('P', 'string', 'exclusionSource', 200),
			},
		],
		[
			'switchLikelihood',
			{
				headerName: 'Likelihood',
				align: 'center',
				...createCellFormatter('P', 'percentage', 'switchLikelihood', 50),
				valueGetter: (val, row) => row?.switchLikelihood ?? null,
				valueFormatter: (value) => (value === 0 ? 'NA' : numToPercentage(value, 0)),
			},
		],
		//
		// misc columns
		//
		[
			'packCvtModified',
			{
				field: 'packCvtModified',
				headerName: 'Pack Cvt Modified',
				renderHeader: () => null,
				disableReorder: true,
				sortable: false,
				valueGetter: (val, row) => row?.matchedProduct?.packCvtModified ?? null,
				renderCell: ({ value }) =>
					value ? (
						<Tooltip placement="top" title="Pack conversion modified">
							<FontAwesomeIcon size="sm" icon={faLightEmergency} />
						</Tooltip>
					) : null,
				minWidth: 35,
				width: 35,
				align: 'center',
				cellClassName: 'packCvtModified-cell',
			},
		],
		[
			'basket',
			{
				field: 'basket',
				headerName: '',
				disableReorder: true,
				hideable: false,
				disableColumnMenu: true,
				sortable: false,
				renderCell: ({ value }) =>
					value ? <FontAwesomeIcon size="sm" icon={faCartShopping} /> : null,
				minWidth: 35,
				width: 35,
				align: 'center',
				cellClassName: 'basket-cell',
			},
		],
		[
			'type',
			{
				field: 'type',
				headerName: '',
				disableReorder: true,
				hideable: false,
				disableColumnMenu: true,
				sortable: false,
				renderCell: ({ value }) => <FontAwesomeIcon size="sm" icon={iconMap[value]} />,
				minWidth: 35,
				width: 35,
				align: 'center',
				cellClassName: ({ value }) => (value === 'NO_MATCH' ? 'type-cell-not-found' : 'type-cell'),
			},
		],
		[
			'hasSimilarProducts',
			{
				field: 'hasSimilarProducts',
				headerName: '',
				disableReorder: true,
				disableColumnMenu: true,
				sortable: false,
				renderCell: ({ value: hasSimilarProducts }: GridRenderCellParams) => (
					<Tooltip
						placement="top"
						title={hasSimilarProducts ? 'Similar products available' : 'No similar products found'}
					>
						<FontAwesomeIcon
							size="sm"
							icon={hasSimilarProducts ? faPuzzlePieceSolid : faPuzzlePieceOutline}
						/>
					</Tooltip>
				),
				minWidth: 35,
				width: 35,
				align: 'center',
				cellClassName: 'hasSimilarProducts-cell',
			},
		],
		//
		// Matched Item Columns
		//
		[
			'matchedRecommendationScore',
			{
				headerName: 'Recommendation',
				align: 'center',
				...createCellFormatter('M', 'percentage', 'matchedRecommendationScore', 50),
				valueGetter: (val, row) => row?.matchedProduct?.recommendationScore ?? null,
				valueFormatter: (value) => (value ? numToPercentage(value, 0) : '--'),
				renderCell: (params) => {
					if (params.row.type === PurchaseGroupMatchType.EXACT) {
						return <FontAwesomeIcon size="sm" icon={faPuzzleSolid} />
					}
					return <span className="MuiDataGrid-cellContent">{params.formattedValue}</span>
				},
			},
		],
		[
			'matchedScore',
			{
				headerName: 'Similarity',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'percentage', 'score', 50),
				valueGetter: (val, row) => row?.matchedProduct?.score ?? null,
			},
		],
		[
			'matchedExclusionSource',
			{
				headerName: 'Exclusion Source',
				...createCellFormatter('M', 'string', 'matchedExclusionSource'),
				valueGetter: (val, row) => row?.matchedProduct?.exclusionSource ?? null,
			},
		],
		[
			'matchedManufacturerName',
			{
				headerName: 'MFG',
				width: 200,
				...createCellFormatter('M', 'string', 'matchedManufacturerName'),
				valueGetter: (val, row) => row?.matchedProduct?.manufacturerName ?? null,
			},
		],
		[
			'matchedDescription',
			{
				headerName: 'Description',
				width: 250,
				...createCellFormatter('M', 'string', 'matchedDescription'),
				valueGetter: (val, row) => row?.matchedProduct?.description ?? null,
			},
		],
		[
			'matchedPackSize',
			{
				headerName: 'Pack',
				...createCellFormatter('M', 'string', 'matchedPackSize', 50),
				valueGetter: (val, row) => row?.matchedProduct?.packSize ?? null,
			},
		],
		[
			'matchedPackSizeConversion',
			{
				headerName: 'Pack Cvt',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'string', 'matchedPackSizeConversion', 50),
				valueGetter: (val, row) => row?.matchedProduct?.packSizeConversion ?? null,
			},
		],
		[
			'matchedDateRangeContractSavings',
			{
				headerName: 'Tot Price Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedDateRangeContractSavings', 150, currency),
				valueGetter: (val, row) => row?.matchedProduct?.dateRangeContractSavings ?? null,
			},
		],
		[
			'matchedDateRangeRebateSavings',
			{
				headerName: 'Tot Rebate Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedDateRangeRebateSavings', 150, currency),
				valueGetter: (val, row) => row?.matchedProduct?.dateRangeRebateSavings ?? null,
			},
		],
		[
			'matchedDateRangeTotalSavings',
			{
				headerName: 'Tot Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedDateRangeTotalSavings', 150, currency),
				valueGetter: (val, row) => row?.matchedProduct?.dateRangeTotalSavings ?? null,
			},
		],
		[
			'matchedAnnualRebate',
			{
				headerName: 'Ann Rebate Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedAnnualRebate', 150, currency),
				valueGetter: (val, row) => row?.matchedProduct?.annualRebate ?? null,
			},
		],
		[
			'matchedAnnualPriceSavings',
			{
				headerName: 'Ann Price Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedAnnualPriceSavings', 150, currency),
				valueGetter: (val, row) => row?.matchedProduct?.annualPriceSavings ?? null,
			},
		],
		[
			'matchedAnnualTotalSavings',
			{
				headerName: 'Ann Tot Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedAnnualTotalSavings', 150, currency),
				valueGetter: (val, row) => row?.matchedProduct?.annualTotalSavings ?? null,
			},
		],
		[
			'matchedSavingsPercentage',
			{
				headerName: 'Savings %',
				type: 'number',
				align: 'right',
				...cellFormatterPct('matchedSavingsPercentage', 50),
				valueGetter: (val, row) => row?.matchedProduct?.savingsPercentage ?? null,
			},
		],

		[
			'matchedStocked',
			{
				field: 'matchedStocked',
				headerName: 'Stocked',
				type: 'boolean',
				minWidth: 50,
				valueGetter: (val, row) => row?.matchedProduct?.stocked ?? null,
			},
		],
		[
			'matchedBrand',
			{
				headerName: 'Brand',
				...createCellFormatter('M', 'string', 'matchedBrand', 50),
				valueGetter: (val, row) => row?.matchedProduct?.brand ?? null,
			},
		],
		[
			'matchedBrandOwner',
			{
				headerName: 'Brd Type',
				...createCellFormatter('M', 'string', 'matchedBrandOwner', 50),
				valueGetter: (val, row) => row?.matchedProduct?.brandOwner ?? null,
			},
		],
		[
			'matchedCategory',
			{
				headerName: 'Cat',
				width: 150,
				...createCellFormatter('M', 'string', 'matchedCategory'),
				valueGetter: (val, row) => row?.matchedProduct?.category ?? null,
			},
		],
		[
			'matchedMpc',
			{
				headerName: 'MPC',
				...createCellFormatter('M', 'string', 'matchedMpc', 50),
				valueGetter: (val, row) => row?.matchedProduct?.mpc ?? null,
			},
		],
		[
			'matchedDpc',
			{
				headerName: 'DPC',
				...createCellFormatter('M', 'string', 'matchedDpc', 50),
				valueGetter: (val, row) => row?.matchedProduct?.dpc ?? null,
			},
		],
		[
			'matchedDeviatedCost',
			{
				headerName: 'Dev Cost',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedDeviatedCost', 120, currency),
				valueGetter: (val, row) => row?.matchedProduct?.deviatedCost ?? null,
			},
		],
		[
			'matchedAdjustedDeviatedCost',
			{
				headerName: 'Adj Dev Cost',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedAdjustedDeviatedCost', 120, currency),
				valueGetter: (val, row) => row?.matchedProduct?.adjustedDeviatedCost ?? null,
			},
		],
		[
			'matchedUnit',
			{
				headerName: 'Unit',
				...createCellFormatter('M', 'string', 'matchedUnit', 50),
				valueGetter: (val, row) => row?.matchedProduct?.unit ?? null,
			},
		],
		[
			'matchedQty',
			{
				headerName: 'QTY',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'number', 'matchedQty', 50),
				valueGetter: (val, row) => row?.matchedProduct?.qty ?? null,
			},
		],
		[
			'matchedVolume',
			{
				headerName: 'Total Volume',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedVolume', 120, currency),
				valueGetter: (val, row) => row?.matchedProduct?.caseVolume ?? null,
			},
		],
		[
			'matchedPerCaseSavings',
			{
				headerName: 'Unit Price Sav',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedPerCaseSavings', 120, currency),
				valueGetter: (val, row) => row?.matchedProduct?.perCaseSavings ?? null,
			},
		],
		[
			'matchedDeviatedPrice',
			{
				headerName: 'Dev Price',
				type: 'number',
				align: 'right',
				...createCellFormatter('M', 'currency', 'matchedDeviatedPrice', 120, currency),
				valueGetter: (val, row) => row?.matchedProduct?.deviatedPrice ?? null,
			},
		],
		[
			'matchedRebateTermSetName',
			{
				headerName: 'Rebate Term Set',
				width: 200,
				...createCellFormatter('M', 'string', 'matchedRebateTermSetName'),
				valueGetter: (val, row) => row?.matchedProduct?.rebateTermSetName ?? null,
			},
		],
		[
			'matchedSavingsTermSetName',
			{
				headerName: 'Pricing Term Set',
				width: 200,
				...createCellFormatter('M', 'string', 'matchedSavingsTermSetName'),
				valueGetter: (val, row) => row?.matchedProduct?.savingsTermSetName ?? null,
			},
		],
	])
}

export const updateColumnOrderPrefs = (
	columns: { field: string; visible: boolean }[],
	oldIndex: number,
	newIndex: number
) => {
	const newColumns = [...columns]
	const [removed] = newColumns.splice(oldIndex, 1)
	newColumns.splice(newIndex, 0, removed)
	return newColumns
}

export const updateColumnVisibilityPrefs = (
	columns: { field: string; visible: boolean }[],
	fields: GridColumnVisibilityModel
) =>
	columns.map((column) => ({
		...column,
		visible: fields[column.field] !== false,
	}))

// remove with feature-art-1621-pal-score-migration ff
export const createColVisibilityModel = (
	prefs: { field: string; visible: boolean }[],
	// remove with feature-art-1621-pal-score-migration ff
	showScoreColumns = false
) => {
	const columnVisibilityModel = {}
	prefs.forEach(({ field, visible }) => {
		columnVisibilityModel[field] = !showScoreColumns && scoreColumns.has(field) ? false : visible
	})
	return columnVisibilityModel
}

export const createColumnsFromPrefs = (
	prefs: { field: string; visible: boolean }[],
	columnMap: Map<string, GridColDef>
) =>
	// Return the columns in the order specified by the user preferences
	prefs.map(({ field }) => columnMap.get(field))

export const columnGroupingModel: GridColumnGroupingModel = [
	{
		groupId: 'Purchased',
		headerAlign: 'center',
		headerClassName: 'default-header',
		children: [
			{ field: 'groupKeyDisplayName' },
			{ field: 'manufacturerName' },
			{ field: 'description' },
			{ field: 'qtyPurchased' },
			{ field: 'packSize' },
			{ field: 'pricePerCase' },
			{ field: 'caseVolume' },
			{ field: 'brandName' },
			{ field: 'brandType' },
			{ field: 'mpc' },
			{ field: 'internalCategoryName' },
			{ field: 'dpc' },
			{ field: 'unit' },
			{ field: 'distributorLocationName' },
			{ field: 'distributorParentName' },
			{ field: 'currentProgramType' },
			{ field: 'exclusionSource' },
			{ field: 'switchLikelihood' },
		],
	},
	{
		groupId: 'Status',
		headerAlign: 'center',
		headerClassName: 'status-header',
		children: [
			{ field: 'packCvtModified' },
			{ field: 'basket' },
			{ field: 'type' },
			{ field: 'hasSimilarProducts' },
		],
	},
	{
		groupId: 'Matched Product',
		headerClassName: 'paper-header',
		headerAlign: 'center',
		children: [
			{ field: 'matchedRecommendationScore' },
			{ field: 'matchedManufacturerName' },
			{ field: 'matchedDescription' },
			{ field: 'matchedPackSize' },
			{ field: 'matchedPackSizeConversion' },
			{ field: 'matchedDateRangeContractSavings' },
			{ field: 'matchedDateRangeRebateSavings' },
			{ field: 'matchedDateRangeTotalSavings' },
			{ field: 'matchedAnnualRebate' },
			{ field: 'matchedAnnualPriceSavings' },
			{ field: 'matchedAnnualTotalSavings' },
			{ field: 'matchedSavingsPercentage' },
			{ field: 'matchedStocked' },
			{ field: 'matchedBrand' },
			{ field: 'matchedBrandOwner' },
			{ field: 'matchedCategory' },
			{ field: 'matchedMpc' },
			{ field: 'matchedDpc' },
			{ field: 'matchedDeviatedCost' },
			{ field: 'matchedAdjustedDeviatedCost' },
			{ field: 'matchedUnit' },
			{ field: 'matchedQty' },
			{ field: 'matchedVolume' },
			{ field: 'matchedPerCaseSavings' },
			{ field: 'matchedDeviatedPrice' },
			{ field: 'matchedRebateTermSetName' },
			{ field: 'matchedSavingsTermSetName' },
			{ field: 'brand' },
			{ field: 'matchedExclusionSource' },
		],
	},
]
